import { zodResolver } from '@hookform/resolvers/zod'
import { useNotification } from '@refinedev/core'
import type { Invitation } from '@sirius/contracts/admin'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAdminClient } from '~/api/adminClientContext'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { errorMessage } from '~/providers/errors'
import type { TenantRef } from '~/tenancy/tenantDecision'
import type { InviteFormOutput, InviteFormValues } from './memberActions'
import {
  invitationsQueryKey,
  inviteFieldError,
  inviteFormSchema,
  inviteRoleOptions,
} from './memberActions'

const EMPTY: InviteFormValues = { email: '', role: 'member' }

/**
 * "Invite": mails someone a link to join the host tenant with a role. Staff
 * may invite an owner, the tenant's owners and admins an admin or member.
 */
export function InviteMemberDialog({
  tenant,
  isPlatformAdmin,
}: {
  tenant: TenantRef
  isPlatformAdmin: boolean
}) {
  const { t } = useTranslation()
  const client = useAdminClient()
  const queryClient = useQueryClient()
  const { open } = useNotification()
  const [isOpen, setIsOpen] = useState(false)
  const form = useForm<InviteFormValues, unknown, InviteFormOutput>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: EMPTY,
  })
  const invite = useMutation({
    mutationFn: (values: InviteFormOutput) =>
      client.invitations.create({ ...values, tenantId: tenant.id }),
    onSuccess: async (invitation: Invitation) => {
      await queryClient.invalidateQueries({
        queryKey: invitationsQueryKey(tenant.id),
      })
      setIsOpen(false)
      open?.({
        type: 'success',
        message: t('settingsPage.invite.success', {
          email: invitation.email,
        }),
      })
    },
    onError: (error) => {
      const fieldError = inviteFieldError(error, t)
      if (fieldError) {
        form.setError(fieldError.field, { message: fieldError.message })
      }
    },
  })
  const showsError = invite.isError && !inviteFieldError(invite.error, t)

  const onOpenChange = (next: boolean) => {
    // no closing while the invitation is sent: its outcome shows here
    if (!next && invite.isPending) {
      return
    }
    if (next) {
      form.reset(EMPTY)
      invite.reset()
    }
    setIsOpen(next)
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <DialogTrigger asChild>
        <Button size="sm">{t('settingsPage.invite.button')}</Button>
      </DialogTrigger>
      <DialogContent>
        <Form {...form}>
          <form
            noValidate
            onSubmit={form.handleSubmit((values) => invite.mutate(values))}
            className="grid gap-4"
          >
            <DialogHeader>
              <DialogTitle>
                {t('settingsPage.invite.title', { tenant: tenant.name })}
              </DialogTitle>
              <DialogDescription>
                {t('settingsPage.invite.description')}
              </DialogDescription>
            </DialogHeader>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('settingsPage.invite.email')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('settingsPage.invite.role')}</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {inviteRoleOptions(isPlatformAdmin).map((role) => (
                        <SelectItem
                          key={role}
                          value={role}
                        >
                          {t(`settingsPage.roles.${role}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            {showsError && (
              <p
                role="alert"
                className="text-destructive text-sm"
              >
                {t('settingsPage.invite.error', {
                  message: errorMessage(invite.error),
                })}
              </p>
            )}
            <DialogFooter>
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={invite.isPending}
                >
                  {t('settingsPage.invite.cancel')}
                </Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={invite.isPending}
              >
                {t('settingsPage.invite.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
